import React, { useState, useEffect } from "react";

import { ReactComponent as ChevronLeft } from "assets/images/iconsV2/chevron-left.svg";
import { ReactComponent as ChevronRight } from "assets/images/iconsV2/chevron-right.svg";

import "./Pagination.scss";

interface PaginationProps {
  /**
   * The total number of pages available based on the data and the page size.
   */
  totalPages: number;

  /**
   * The currently active page number.
   */
  currentPage: number;

  /**
   * A callback function that is triggered when a page number is clicked.
   * This function receives the page number as an argument and can be used to handle page changes.
   */
  pageClicked: (page: number) => void;
}

function Pagination({ totalPages, currentPage, pageClicked }: PaginationProps) {
  const [pageArray, setPageArray] = useState<(number | string)[]>([]);

  useEffect(() => {
    const updatePageArray = () => {
      const width = window.innerWidth;
      let visiblePages: number;

      if (width >= 992) {
        visiblePages = 9;
      } else if (width >= 320) {
        visiblePages = 7;
      } else {
        visiblePages = 5;
      }

      let pageArr: (number | string)[] = [];

      if (totalPages > 1) {
        if (totalPages <= visiblePages) {
          pageArr = Array.from({ length: totalPages }, (_, i) => i + 1);
        } else if (currentPage <= Math.floor(visiblePages / 2)) {
          pageArr = [...Array.from({ length: visiblePages - 2 }, (_, i) => i + 1), "...", totalPages];
        } else if (totalPages - currentPage < Math.floor(visiblePages / 2)) {
          pageArr = [
            1,
            "...",
            ...Array.from({ length: visiblePages - 3 }, (_, i) => totalPages - (visiblePages - 3) + i),
            totalPages,
          ];
        } else {
          pageArr = [
            1,
            "...",
            ...Array.from({ length: visiblePages - 4 }, (_, i) => currentPage - Math.floor((visiblePages - 4) / 2) + i),
            "...",
            totalPages,
          ];
        }

        if (pageArr[pageArr.length - 1] !== totalPages) {
          pageArr = pageArr.slice(0, visiblePages - 1).concat(totalPages as any);
        }
      }

      if (pageArr.length === 0) pageArr = [1];
      setPageArray(pageArr);
    };

    updatePageArray();

    window.addEventListener("resize", updatePageArray);
    return () => window.removeEventListener("resize", updatePageArray);
  }, [totalPages, currentPage]);

  return (
    <div className="paginationV2">
      <div
        className="chevron chevron-left"
        onClick={() => {
          if (currentPage > 1) pageClicked(currentPage - 1);
        }}
      >
        <ChevronLeft width={20} height={20} />
      </div>

      <div className="pagination-buttons text-sm">
        {pageArray.map((page, index) => (
          <button
            className={currentPage === page ? "active" : ""}
            key={index}
            type="button"
            onClick={() => typeof page === "number" && pageClicked(page)}
            disabled={typeof page !== "number"}
          >
            <div className="pagination-number">{page}</div>
          </button>
        ))}
      </div>
      <div
        className="chevron chevron-right"
        onClick={() => {
          if (currentPage !== totalPages) pageClicked(currentPage + 1);
        }}
      >
        <ChevronRight width={20} height={20} />
      </div>
    </div>
  );
}

export default Pagination;
